import React, { useContext, useEffect, useMemo, useState } from 'react';
import { AlarmRecordsAnalysisContext } from '../..';
import { useRequest } from 'ahooks';
import styles from './index.module.scss';
import CardBox from '../CardBox';
import device_rank_icon from '../../imgs/device_rank_icon.png';
import { apiV2AlarmRecordAnalysisAlarmDeviceRankingPost } from '@maxtropy/device-customer-apis-v2';
import { EchartsWithTheme, Empty, Tooltip, useTheme } from '@maxtropy/components';
import { getChartOption } from './charts';
import { Progress, Spin } from 'antd';
import classNames from 'classnames';
import { keepZeroOrLine } from '@/utils/utils';
import { getDefaultTime } from '../../utils';
import { DateType } from '../../const';

const HighDeviceAlarmRanking = () => {
  const {
    timeResolution,
    value,
    activeKey,
    selectedSceneId,
    setSearchParams,
    searchParams,
    setAlarmModalOpen,
    sceneTabList,
  } = useContext(AlarmRecordsAnalysisContext);
  const theme = useTheme();
  const [selectedId, setSelectedId] = useState<number>();

  // 报警设备排名
  const { data: alarmRankingData, loading } = useRequest(
    async () => {
      return await apiV2AlarmRecordAnalysisAlarmDeviceRankingPost({
        sceneId: selectedSceneId,
        startTime: getDefaultTime(timeResolution!, value?.[0]).startTime,
        endTime: getDefaultTime(timeResolution!, value?.[1]).endTime,
        timeResolution: timeResolution === DateType.CUSTOM ? 'D' : timeResolution,
        state: activeKey === 'all' ? undefined : activeKey,
      });
    },
    {
      ready: !!timeResolution && !!value && !!activeKey,
      refreshDeps: [timeResolution, selectedSceneId, value, activeKey],
    }
  );

  // 默认选中第一个
  useEffect(() => {
    if (!alarmRankingData?.list?.[0]) return;
    setSelectedId(alarmRankingData?.list?.[0]?.deviceId);
  }, [alarmRankingData]);

  const selecedChartData = useMemo(() => {
    return alarmRankingData?.list?.find(item => item.deviceId === selectedId);
  }, [alarmRankingData, selectedId]);

  // 图表
  const lineChartOption = useMemo(() => {
    return getChartOption(selecedChartData?.detail, selecedChartData?.deviceName);
  }, [alarmRankingData, selecedChartData]);

  const total = useMemo(() => {
    return (alarmRankingData?.list ?? []).reduce((sum, item) => sum + (item?.alarmCount ?? 0), 0);
  }, [alarmRankingData]);

  return (
    <CardBox title="高频设备报警排名">
      <Spin spinning={loading}>
        {!alarmRankingData?.list || alarmRankingData?.list?.length === 0 ? (
          <Empty />
        ) : (
          <>
            <div className={styles.top_rank_list_wrapper}>
              {(alarmRankingData?.list ?? []).map((item, index) => (
                <div
                  onMouseEnter={() => setSelectedId(item.deviceId)}
                  className={classNames(styles.top_rank_list_item, selectedId === item.deviceId ? styles.selected : '')}
                  key={item.deviceId}
                  onClick={() => {
                    setSelectedId(item.deviceId);
                    setSearchParams?.({
                      ...searchParams,
                      startTime: getDefaultTime(timeResolution!, value?.[0]).startTime,
                      endTime: getDefaultTime(timeResolution!, value?.[1]).endTime,
                      deviceName: item.deviceName,
                      deviceLabelIds: selectedSceneId ? [selectedSceneId] : (sceneTabList ?? []).map(i => i.id!),
                    });
                    setAlarmModalOpen?.(true);
                  }}
                >
                  <span className={styles.rank_num}>{index + 1}</span>
                  <img className={styles.rank_icon} src={device_rank_icon} alt="" />
                  <Tooltip title={item.deviceName}>
                    <span className={styles.device_name}>{item.deviceName}</span>
                  </Tooltip>
                  <div className={styles.progress}>
                    <Progress
                      percent={total === 0 ? 0 : ((item.alarmCount ?? 0) / total) * 100}
                      showInfo={false}
                      size="small"
                      strokeColor={'#FFCB47'}
                    />
                  </div>
                  <span className={styles.alarm_num}>{item.alarmCount}</span>
                </div>
              ))}
            </div>
            <div className={styles.bottom_bar_wrapper}>
              <div className={styles.bottom_count}>
                <div className={styles.label}>{selecedChartData?.deviceName + '-报警趋势'}</div>
                <div className={styles.count}>
                  总计(次)：<span className={styles.count_num}>{keepZeroOrLine(selecedChartData?.alarmCount)}</span>
                </div>
              </div>
              <EchartsWithTheme
                empty={{
                  isShowEmpty: !alarmRankingData,
                  emptyNode: <Empty />,
                }}
                loading={loading}
                theme={theme}
                style={{ height: 160 }}
                option={lineChartOption}
              />
            </div>
          </>
        )}
      </Spin>
    </CardBox>
  );
};

export default HighDeviceAlarmRanking;
